import { Button, Popconfirm, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { Edit, Plus, Trash, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AlternateUserFilter from '../../components/AlternateUserFilter';
import AlternateUserModal from '../../components/AlternateUserModal';
import DateTimeTag from '../../components/DateTimeTag';
import { pageVariants } from '../../constants';
import { getRoutes } from '../../constants/Routes';
import useAuth from '../../hooks/useAuth';
import { deleteAlternateUser, getAlternateUsers } from '../../redux/actions/alternate-user';
import { setAlternateFilters } from '../../redux/slices/filtersSlice';
import { store } from '../../redux/store';
import { getFullName } from '../../utils';

const AlternateUsers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { alternateUser: users, alternateUserPagination: pagination } = useSelector(
    (state) => state.alternateUserSlice
  );
  const { alternateFilters: filters } = useSelector((state) => state.filtersSlice);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState();

  const columns = [
    {
      title: 'Primary User',
      dataIndex: 'primary_user',
      key: 'primary_user',
      render: (user) => getFullName(user),
    },
    {
      title: 'Secondary User',
      dataIndex: 'secondary_user',
      key: 'secondary_user',
      render: (user) => getFullName(user),
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (date) => date && <DateTimeTag date={date} />,
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date) => date && <DateTimeTag date={date} />,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 300,
      render: (text) => (
        <Typography.Paragraph
          ellipsis={{ rows: 1, tooltip: true }}
          style={{ margin: 0, maxWidth: 250 }}
        >
          {text}
        </Typography.Paragraph>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_, record) => {
        const now = dayjs();
        const start = dayjs(record?.start_date);
        const end = dayjs(record?.end_date);

        let status = '';
        let type = '';

        if (now.isBefore(start)) {
          status = 'Upcoming';
          type = 'warning';
        } else if (now.isAfter(end)) {
          status = 'Expired';
          type = 'danger';
        } else {
          status = 'Active';
          type = 'success';
        }

        return (
          <Typography.Text strong type={type}>
            {status}
          </Typography.Text>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'end',
      width: 100,
      render: (_, record) => (
        <Space>
          <Edit
            size={18}
            color="#6B7280"
            cursor="pointer"
            onClick={() => {
              setOpen(true);
              setData(record);
            }}
          />
          <Popconfirm
            title="Remove Assignment"
            description="Are you sure you want to remove this?"
            onConfirm={() => handleDeleteUser(record?.id)}
          >
            <Trash size={18} color="#6B7280" cursor="pointer" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handlePaginationChange = async (page) => {
    await getAlternateUsers({ ...filters, page });
  };

  const handleDeleteUser = (id) => {
    deleteAlternateUser(id);
  };

  useEffect(() => {
    getAlternateUsers();
  }, []);

  return (
    <motion.div
      className="Users"
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={pageVariants}
    >
      <div className="header-flex">
        <div className="page-title truncate">Alternate Users</div>
        {user?.role !== 2 && (
          <Space>
            <Button
              variant="filled"
              color="cyan"
              size="large"
              icon={<Users />}
              onClick={() => navigate(getRoutes().users.route())}
            />
            <Button
              variant="solid"
              color="primary"
              size="large"
              icon={<Plus />}
              onClick={() => setOpen(true)}
            >
              Add Alternate User
            </Button>
          </Space>
        )}
      </div>
      <div className="theme-bg shadow">
        <motion.div
          className="filter-header p-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
        >
          <AlternateUserFilter
            onFiltersChange={(filters) => store.dispatch(setAlternateFilters(filters))}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.4 } }}
        >
          <Table
            columns={columns}
            dataSource={users}
            rowKey="id"
            components={{
              header: {
                cell: (props) => <th {...props} className="table-header px-2" />,
              },
              body: {
                cell: (props) => <td {...props} className="theme-bg px-2 py-1" />,
              },
            }}
            pagination={{
              current: pagination?.page,
              pageSize: pagination?.limit,
              total: pagination?.total,
              showSizeChanger: false,
              onChange: handlePaginationChange,
              position: ['bottomCenter'],
            }}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 360px)' }}
          />
        </motion.div>
      </div>
      {open && (
        <AlternateUserModal
          open={open}
          onCancel={() => {
            setData();
            setOpen(false);
          }}
          data={data}
        />
      )}
    </motion.div>
  );
};

export default AlternateUsers;
